import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";

import { useUser } from "../../user/utils/user";

import { AgGridWithOverlay } from "../../common/components/AgGridWithOverlay";
import LoadingModal from "../../common/components/LoadingModal.js";
import DateInput from "../../common/components/form/DateInput.js";
import Select from "../../common/components/form/Select.js";
import { useRoleClientNavigate } from "../../common/hooks/useRoleClientNavigate";
import { get } from "../../common/utils/api";
import { formatDate } from "../../common/utils/helpers";

function ReviewForm({ userOptions, t, groupOptions, countryOptions }) {
  return (
    <React.Fragment>
      <div className="space-y-6 pt-8 sm:space-y-5 sm:pt-10">
        <div className="space-y-6 sm:space-y-5">
          <Select
            label={"User email"}
            name={"formUser"}
            options={userOptions}
          />
          <DateInput label={"Start date"} name={"formStartDate"} />
          <DateInput label={"End date"} name={"formEndDate"} />
          <Select label={"Group"} name={"formGroup"} options={groupOptions} />
          <Select
            label={"Country"}
            name={"formCountry"}
            options={countryOptions}
          />

          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-2 sm:border-t sm:border-gray-200 sm:pt-5">
            <label
              htmlFor={"formShowCompleted"}
              className="block sm:col-span-2 text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
            >
              Include already-reviewed days
            </label>
            <div className="block sm:col-span-1 text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
              <Field
                type="checkbox"
                id={"formShowCompleted"}
                name={"formShowCompleted"}
              ></Field>
            </div>
            <label
              htmlFor={"formCropYield"}
              className="block sm:col-span-2 text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
            >
              Require yield
            </label>
            <div className="block sm:col-span-1 text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
              <Field
                type="checkbox"
                id={"formCropYield"}
                name={"formCropYield"}
              ></Field>
            </div>
          </div>
        </div>
      </div>
      <div className="py-5">
        <div className="flex justify-end">
          <button
            type="submit"
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {t("common.generate", "Generate Report")}
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}

function ReviewSummary({ showAlert, t }) {
  let [summaryData, setSummaryData] = useState(null);
  let [userOptions, setUserOptions] = useState([]);
  let [groupOptions, setGroupOptions] = useState([]);
  let [countryOptions, setCountryOptions] = useState([]);

  const [error, setError] = useState(null);

  const navigate = useRoleClientNavigate();
  const { user } = useUser();

  // Default date range: past three days for now
  let today = new Date();
  let daysBefore = new Date();
  daysBefore.setDate(today.getDate() - 3);

  // Use session storage for the existing user and dates if they exist
  const initialValues = {
    formUser: sessionStorage.getItem("user") || "All",
    formGroup: sessionStorage.getItem("group") || "All",
    formCountry: sessionStorage.getItem("country") || "All",
    formStartDate:
      sessionStorage.getItem("startDate") || formatDate(daysBefore),
    formEndDate: sessionStorage.getItem("endDate") || formatDate(today),
    formShowCompleted:
      sessionStorage.getItem("showCompleted") == "yes" ||
      sessionStorage.getItem("showCompleted") == "true" ||
      false,
    formCropYield: sessionStorage.getItem("cropYield") == "true" || false,
  };
  const getGroups = async () => {
    get("/users/groups/?old_review=true").then((response) => {
      setGroupOptions([
        "All",
        ...response.data.sort((a, b) => a.localeCompare(b)),
      ]);
    });
  };

  const getCountries = async () => {
    get("/users/countries/").then((response) => {
      setCountryOptions(["All", ...response.data.map((c) => c.name)]);
    });
  };

  let summaryColumnDefs = [
    {
      field: "review",
      headerName: "",
      width: 80,
      cellRenderer: () => t("plot.review", "review"),
      onCellClicked: function (params) {
        navigate(
          `/review/single?user=${params.data.email}&date=${params.data.date}`,
        );
      },
      cellClass: ["text-cyan-600", "hover:text-cyan-900", "cursor-pointer"],
    },
    { field: "user", headerName: t("user.user", "User") },
    { field: "email", headerName: t("user.email", "Email") },
    {
      field: "date",
      headerName: t("common.createddate", "Date"),
      width: 120,
    },
    {
      field: "review_rate",
      headerName: t("user.review_rate", "Own review rate"),
      width: 150,
      valueFormatter: (params) =>
        (params.data.review_rate * 100).toFixed(0) + "%",
      type: "numericColumn",
    },
    {
      field: "any_review_rate",
      headerName: t("user.any_review_rate", "Any review rate"),
      width: 150,
      valueFormatter: (params) =>
        (params.data.any_review_rate * 100).toFixed(0) + "%",
      type: "numericColumn",
    },
    {
      field: "approved",
      headerName: t("user.approved", "approved"),
      width: 120,
      type: "numericColumn",
    },
    {
      field: "reviewed",
      headerName: t("user.reviewed", "reviewed"),
      width: 120,
      type: "numericColumn",
    },
    {
      field: "total",
      headerName: t("user.total", "total"),
      width: 120,
      type: "numericColumn",
    },
  ];

  const getUsers = async () => {
    get("/users/").then((response) => {
      let uniqueUsers = Object.assign(
        { All: "All" },
        ...response.data
          .sort(function (a, b) {
            return a.first_name.localeCompare(b.first_name);
          })
          .map((x) => ({
            [x.email]: x.first_name + " " + x.last_name + " (" + x.email + ")",
          })),
      );
      setUserOptions(uniqueUsers);
    });
  };

  const getData = async (
    user_selected,
    group_selected,
    country_selected,
    start_date,
    end_date,
    show_completed,
    crop_yield,
    setSubmitting,
  ) => {
    const params = new URLSearchParams({
      username: user_selected,
      start_date: start_date,
      end_date: end_date,
      group: group_selected,
      country: country_selected,
      yield: crop_yield,
    });
    get(`/data/review/?${params.toString()}`)
      .then((response) => response.data)
      .then((data) => {
        data.forEach((d) => {
          d.user = d.first_name + " " + d.last_name;
          d.approved = d.reviewed - d.rejected;
          d.review_rate = d.total === 0 ? 0 : d.reviewed / d.total;
          d.any_review_rate = d.total === 0 ? 0 : d.any_reviewed / d.total;
        });

        // Show most recent data first, with user as the tiebreaker
        data = data.sort(function (a, b) {
          let order1 = a.email.localeCompare(b.email);
          let order2 = a.date.localeCompare(b.date);

          if (order2 === 0) {
            return order1;
          } else {
            return -order2;
          }
        });

        data = data.filter((d) => {
          if (show_completed === "no") {
            return d["total"] > d["reviewed"];
          }
          return true;
        });
        setSummaryData(data);
      })
      .catch((error) => {
        console.error(error);
        setError(error);
      })
      .finally(() => setTimeout(() => setSubmitting(false), 300));
  };

  const grid = useRef();
  const onGridReady = (params) => {
    grid.current = params.api;
    params.api.sizeColumnsToFit();
  };

  useEffect(() => {
    getUsers();
    getGroups();
    getCountries();
  }, []);

  function onSubmit(value, { setSubmitting }) {
    getData(
      value.formUser,
      value.formGroup,
      value.formCountry,
      value.formStartDate,
      value.formEndDate,
      value.formShowCompleted,
      value.formCropYield,
      setSubmitting,
    );
    sessionStorage.setItem("user", value.formUser);
    sessionStorage.setItem("group", value.formGroup);
    sessionStorage.setItem("country", value.formCountry);
    sessionStorage.setItem("startDate", value.formStartDate);
    sessionStorage.setItem("endDate", value.formEndDate);
    sessionStorage.setItem("showCompleted", value.formShowCompleted);
    sessionStorage.setItem("cropYield", value.formCropYield);
  }

  return (
    <React.Fragment>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 bg-white border-gray-200 shadow rounded-md pt-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.summary", "Generate Review Summary")}
            </h3>
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <React.Fragment>
                <LoadingModal open={isSubmitting} />
                <Form className="space-y-8 divide-y divide-gray-200">
                  <ReviewForm
                    user={user}
                    userOptions={userOptions}
                    groupOptions={groupOptions}
                    countryOptions={countryOptions}
                    t={t}
                  />
                </Form>
              </React.Fragment>
            )}
          </Formik>
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.reviewSummary", "Review Summary")}
            </h3>
          </div>
        </div>
        <div className="ag-theme-alpine mt-4" style={{ height: 400 }}>
          <AgGridWithOverlay
            ref={grid}
            rowData={summaryData}
            columnDefs={summaryColumnDefs}
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
            onGridReady={onGridReady}
            error={error}
            t={t}
          ></AgGridWithOverlay>
        </div>
      </div>
    </React.Fragment>
  );
}
export default withTranslation()(ReviewSummary);
